// Generated by TaroBase SDK Generator: 2026-10-04T15:00:51.103Z
// Collection: prizePoolRaffles/$raffleId
// Edits may be overwritten by subsequent runs!

import {
  get, getMany, set, subscribe, getFiles, setFile, runQuery, count, aggregate, transformValues,
  Time, Token, Increment, Address, PublicKey,
  TimeOperation, IncrementOperation, TokenAmount, AddressType,
  TarobaseMetadata, DocumentOperation, FileItem, setMany, formatError,
} from '../db-client';
import type { AggregateResult, AggregateOperation } from '../db-client';

/**
 * Monthly raffle payout records. Only the backend (PROJECT_VAULT_ADDRESS) can create these, triggering token transfers from the contract vault to the raffle winner. One record per token per pool per month.
 */

export interface PrizePoolRafflesRequest {
  poolType: string;
  winner: AddressType;
  tokenMint: string;
  amount: number | TimeOperation | IncrementOperation | TokenAmount;
  month: string;
  ts: number | TimeOperation | IncrementOperation | TokenAmount;
}

export interface PrizePoolRafflesResponse {
  poolType: string;
  winner: string;
  tokenMint: string;
  amount: number;
  month: string;
  ts: number;
  id: string;
  tarobase_created_at: number;
  tarobase_transaction_hash?: string | undefined; // Represents the hash of the transaction that created this item. Can be used to create Solscan links (combine with the current network in the Tarobase app)
}

/**
 * Build a PrizePoolRaffles operation for use with setMany.
 * @returns A DocumentOperation that can be passed to setMany.
 */
export function buildPrizePoolRaffles(raffleId: string, data?: PrizePoolRafflesRequest): DocumentOperation {
  const doc = data ? transformValues(data) : {};
  return { path: `prizePoolRaffles/${raffleId}`, document: doc };
}

/**
 * Only the backend (PROJECT_VAULT_ADDRESS) can create raffle payout records. When created, the onchain hook transfers the accumulated tokens from the contract vault to the winning user. Fields: poolType (top1/top10/top100), winner (wallet address), tokenMint (SOL/BONK/SKR), amount in base units, month (e.g. 2026-04), ts (timestamp). (Create/Update Single Item)
 * @returns A boolean indicating whether the operation succeeded (true) or failed (false). Always check this value to confirm the write worked.
 */
export async function setPrizePoolRaffles(raffleId: string, data?: PrizePoolRafflesRequest): Promise<boolean> {
  try {
    const transformedData = transformValues(data || {});
    const succeeded = await set(`prizePoolRaffles/${raffleId}`, transformedData);
    return !!succeeded;
  } catch (error) {
    console.error(`Error setting PrizePoolRaffles: ${formatError(error)}`);
    return false;
  }
}

/**
 * 
  Read Operation Details: Anyone can view past raffle results to see winners and payouts.
   (Get Single Item)
 */
export async function getPrizePoolRaffles(raffleId: string): Promise<PrizePoolRafflesResponse | null> {
  try {
    const result = await get(`prizePoolRaffles/${raffleId}`);
    return result as PrizePoolRafflesResponse | null;
  } catch (error) {
    console.error(`Error getting PrizePoolRaffles: ${formatError(error)}`);
    return null;
  }
}

/**
 * Subscribes to changes in a single PrizePoolRaffles document. (
  Read Operation Details: Anyone can view past raffle results to see winners and payouts.
  )
 */
export function subscribePrizePoolRaffles(callback: (data: PrizePoolRafflesResponse | null) => void, raffleId: string): Promise<() => Promise<void>> {
  return subscribe(`prizePoolRaffles/${raffleId}`, { // Use the item's basePath
    onData: (data) => {
      callback(data as PrizePoolRafflesResponse | null);
    },
    onError: (error) => {
      console.error(`Error subscribing to PrizePoolRaffles at path '${`prizePoolRaffles/${raffleId}`}': ${formatError(error)}`);
    }
  });
}

/**
 * Get many PrizePoolRaffles items from collection prizePoolRaffles
 
  Read Operation Details: Anyone can view past raffle results to see winners and payouts.
  
 */
export async function getManyPrizePoolRaffles(filter: string = ""): Promise<PrizePoolRafflesResponse[]> {
  try {
    const results = await get("prizePoolRaffles", { prompt: filter });
    return Array.isArray(results) ? results as PrizePoolRafflesResponse[] : [];
  } catch (error) {
    console.error(`Error getting PrizePoolRaffles collection at path 'prizePoolRaffles': ${formatError(error)}`);
    return [];
  }
}

/**
 * Subscribe to changes in PrizePoolRaffles collection at prizePoolRaffles
 
  Read Operation Details: Anyone can view past raffle results to see winners and payouts.
  
 */
export function subscribeManyPrizePoolRaffles(callback: (data: PrizePoolRafflesResponse[]) => void, filter: string = ""): Promise<() => Promise<void>> {
  return subscribe("prizePoolRaffles", {
    prompt: filter,
    onData: (data) => {
      callback(Array.isArray(data) ? data as PrizePoolRafflesResponse[] : []);
    },
    onError: (error) => {
       console.error(`Error subscribing to PrizePoolRaffles collection at path '${"prizePoolRaffles"}': ${formatError(error)}`);
    }
  });
}

/**
 * Get all PrizePoolRaffles items from collection prizePoolRaffles
 
  Read Operation Details: Anyone can view past raffle results to see winners and payouts.
  
 */
export async function getAllPrizePoolRaffles(filter: string = ""): Promise<PrizePoolRafflesResponse[]> {
  try {
    const results = await get("prizePoolRaffles", { prompt: filter });
    return Array.isArray(results) ? results as PrizePoolRafflesResponse[] : [];
  } catch (error) {
    console.error(`Error getting PrizePoolRaffles collection at path 'prizePoolRaffles': ${formatError(error)}`);
    return [];
  }
}

/**
 * Subscribe to changes in PrizePoolRaffles collection at prizePoolRaffles
 
  Read Operation Details: Anyone can view past raffle results to see winners and payouts.
  
 */
export function subscribeAllPrizePoolRaffles(callback: (data: PrizePoolRafflesResponse[]) => void, filter: string = ""): Promise<() => Promise<void>> {
  return subscribe("prizePoolRaffles", {
    prompt: filter,
    onData: (data) => {
      callback(Array.isArray(data) ? data as PrizePoolRafflesResponse[] : []);
    },
    onError: (error) => {
       console.error(`Error subscribing to PrizePoolRaffles collection at path '${"prizePoolRaffles"}': ${formatError(error)}`);
    }
  });
}

/**
 * Count PrizePoolRaffles items in collection prizePoolRaffles.
 * Returns a numeric count instead of fetching full documents — much more efficient.
 *
 * NOTE: This only works because the read policy for this collection is "true".
 * @param filter - Optional natural language filter (e.g., "created in the last 7 days")
 * @returns AggregateResult with the count value
 */
export async function countPrizePoolRaffles(filter: string = ""): Promise<AggregateResult> {
  try {
    return await count("prizePoolRaffles", { prompt: filter || undefined });
  } catch (error) {
    console.error(`Error counting PrizePoolRaffles at path 'prizePoolRaffles': ${formatError(error)}`);
    return { value: 0 };
  }
}

/**
 * Run an aggregate operation on PrizePoolRaffles items in collection prizePoolRaffles.
 * Returns a single numeric value instead of fetching full documents.
 *
 * Supported operations: 'count', 'uniqueCount', 'sum', 'avg', 'min', 'max'.
 * For 'sum', 'avg', 'min', 'max', and 'uniqueCount', you must provide the field name.
 *
 * NOTE: This only works because the read policy for this collection is "true".
 * @param operation - The aggregate operation to perform
 * @param opts - Options including optional filter prompt and field name
 * @returns AggregateResult with the computed numeric value
 */
export async function aggregatePrizePoolRaffles(operation: AggregateOperation, opts?: { filter?: string; field?: string }): Promise<AggregateResult> {
  try {
    return await aggregate("prizePoolRaffles", operation, { prompt: opts?.filter || undefined, field: opts?.field });
  } catch (error) {
    console.error(`Error aggregating PrizePoolRaffles at path 'prizePoolRaffles': ${formatError(error)}`);
    return { value: 0 };
  }
}

